import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { resolveImage, formatApiError, formatRM } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { usePageTitle } from "@/contexts/PageTitlesContext";
import { toast } from "sonner";
import { Loader2, CalendarDays, Clock, User2 } from "lucide-react";

const YEARS = Array.from({ length: 90 }, (_, i) => 2015 - i);
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const HOURS = Array.from({ length: 24 }, (_, i) => i);

export default function Booking() {
  const { t, i18n } = useTranslation();
  const zh = !i18n.language?.startsWith("en");
  const { user } = useAuth();
  const navigate = useNavigate();
  const pt = usePageTitle("booking", { title_zh: t("booking.title"), title_en: t("booking.title"), subtitle_zh: t("booking.subtitle"), subtitle_en: t("booking.subtitle") });
  const [masters, setMasters] = useState([]);
  const [pickedMaster, setPickedMaster] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({
    name: "", phone: "", contact_email: "", concern: "",
    gender: "", birth_year: "", birth_month: "", birth_day: "", birth_hour: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/masters").then((r) => setMasters(r.data)).catch(() => setMasters([]));
  }, []);

  useEffect(() => {
    if (!pickedMaster) { setSlots([]); return; }
    api.get("/booking-slots", { params: { master_id: pickedMaster.id } })
      .then((r) => setSlots(r.data.filter((s) => !s.is_booked)))
      .catch(() => setSlots([]));
    setSelectedDate(null); setSelectedSlot(null);
  }, [pickedMaster]);

  useEffect(() => {
    if (user) setForm((f) => ({
      ...f,
      name: f.name || user.name || "",
      phone: f.phone || user.phone || "",
      contact_email: f.contact_email || user.email || "",
      gender: f.gender || user.gender || "",
      birth_year: f.birth_year || user.birth_year || "",
      birth_month: f.birth_month || user.birth_month || "",
      birth_day: f.birth_day || user.birth_day || "",
      birth_hour: f.birth_hour === "" && user.birth_hour !== null && user.birth_hour !== undefined ? user.birth_hour : f.birth_hour,
    }));
  }, [user]);

  const byDate = useMemo(() => {
    const map = {};
    for (const s of slots) {
      const k = (s.starts_at || "").slice(0, 10);
      if (!map[k]) map[k] = [];
      map[k].push(s);
    }
    return map;
  }, [slots]);
  const dates = Object.keys(byDate).sort();

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/login?next=/booking"); return; }
    setSubmitting(true);
    try {
      const payload = {
        slot_id: selectedSlot.id,
        name: form.name,
        phone: form.phone,
        contact_email: form.contact_email,
        concern: form.concern,
        gender: form.gender || null,
        birth_year: form.birth_year ? Number(form.birth_year) : null,
        birth_month: form.birth_month ? Number(form.birth_month) : null,
        birth_day: form.birth_day ? Number(form.birth_day) : null,
        birth_hour: form.birth_hour === "" || form.birth_hour === null || form.birth_hour === undefined ? null : Number(form.birth_hour),
      };
      const { data } = await api.post("/bookings", payload);
      toast.success(t("common.saved"));
      // Land directly on the payment (booking detail) page
      navigate(`/bookings/${data.id}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const inputCls = "w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none bg-white transition-colors duration-200";

  return (
    <div className="mx-auto max-w-5xl px-6 lg:px-10 py-16" data-testid="booking-page">
      <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold" data-testid="booking-subtitle">{zh ? pt.subtitle_zh : pt.subtitle_en}</div>
      <h1 className="mt-3 font-serif-cn text-4xl text-[#003645] font-semibold tracking-tight" data-testid="booking-title">{zh ? pt.title_zh : pt.title_en}</h1>
      <p className="mt-4 text-sm text-[#4a6e77]">{t("booking.depositNotice")}</p>

      {/* Step 1: pick master */}
      <div className="mt-10" data-testid="booking-master-picker">
        <div className="text-xs uppercase tracking-widest text-[#4a6e77] mb-3">{t("masters.pickMaster")}</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {masters.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setPickedMaster(m)}
              className={`text-left rounded-lg border transition-colors duration-200 p-4 flex gap-3 ${pickedMaster?.id === m.id ? "border-[#005870] bg-[#005870]/5" : "border-[#e5ebeb] bg-white hover:border-[#00868e]"}`}
              data-testid={`master-card-${m.id}`}
            >
              <div className="h-16 w-16 rounded-full overflow-hidden bg-[#f5f0e8] shrink-0 flex items-center justify-center">
                {m.photo ? <img src={resolveImage(m.photo)} alt="" className="h-full w-full object-cover" /> : <User2 size={24} className="text-[#4a6e77]" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-serif-cn font-medium text-[#003645] truncate">{zh ? m.name_zh : m.name_en}</div>
                  <div className="text-[#005870] font-semibold text-sm shrink-0" data-testid={`master-price-${m.id}`}>{formatRM(m.price || 0)}</div>
                </div>
                <div className="text-xs text-[#4a6e77] mt-1 line-clamp-3">{zh ? m.bio_zh : m.bio_en}</div>
              </div>
            </button>
          ))}
          {masters.length === 0 && (
            <div className="col-span-full rounded-lg border border-dashed border-[#e5ebeb] bg-white/50 py-12 px-6 text-center" data-testid="booking-empty-state">
              <div className="font-serif-cn text-lg text-[#003645] mb-1">{zh ? "暂未开放" : "Not yet available"}</div>
              <div className="text-sm text-[#4a6e77]">
                {zh ? "请稍后再来，我们正在筹备命理师傅名单。" : "Please check back later — masters are being prepared."}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Step 2: pick date/time */}
      {pickedMaster && (
        <>
          {dates.length === 0 && (
            <div className="mt-10 rounded-lg border border-[#e5ebeb] bg-white p-10 text-center text-[#4a6e77]">{t("booking.noSlots")}</div>
          )}
          {dates.length > 0 && (
            <div className="mt-10" data-testid="booking-date-picker">
              <div className="text-xs uppercase tracking-widest text-[#4a6e77] mb-3">{t("booking.pickDate")}</div>
              <div className="flex flex-wrap gap-2">
                {dates.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => { setSelectedDate(d); setSelectedSlot(null); }}
                    className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors duration-200 ${selectedDate === d ? "bg-[#005870] text-white border-[#005870]" : "bg-white text-[#003645] border-[#e5ebeb] hover:border-[#00868e]"}`}
                    data-testid={`date-${d}`}
                  >
                    <CalendarDays size={14} className="inline mr-1.5" />
                    {new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", weekday: "short" })}
                  </button>
                ))}
              </div>

              {selectedDate && (
                <div className="mt-6" data-testid="booking-time-picker">
                  <div className="text-xs uppercase tracking-widest text-[#4a6e77] mb-3">{t("booking.pickTime")}</div>
                  <div className="flex flex-wrap gap-2">
                    {(byDate[selectedDate] || []).map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setSelectedSlot(s)}
                        className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors duration-200 ${selectedSlot?.id === s.id ? "bg-[#005870] text-white border-[#005870]" : "bg-white text-[#003645] border-[#e5ebeb] hover:border-[#00868e]"}`}
                        data-testid={`slot-${s.id}`}
                      >
                        <Clock size={14} className="inline mr-1.5" />
                        {new Date(s.starts_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · {s.duration_min} {t("booking.minutes")}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {selectedSlot && (
                <form onSubmit={submit} className="mt-8 rounded-lg border border-[#e5ebeb] bg-white p-6 space-y-4" data-testid="booking-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input required placeholder={t("auth.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="booking-name" />
                    <input required placeholder={t("auth.phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="booking-phone" />
                    <input
                      required
                      type="email"
                      placeholder={t("auth.email")}
                      value={form.contact_email}
                      onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                      className={inputCls + " sm:col-span-2"}
                      data-testid="booking-email"
                    />
                  </div>

                  {/* Birth data */}
                  <div className="pt-4 border-t border-[#e5ebeb]">
                    <div className="text-xs uppercase tracking-widest text-[#00868e] font-semibold mb-3">{t("profile.birthDate")}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                      <select className={inputCls} value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} data-testid="booking-gender">
                        <option value="">{t("profile.gender")}</option>
                        <option value="male">{t("profile.male")}</option>
                        <option value="female">{t("profile.female")}</option>
                        <option value="other">{t("profile.other")}</option>
                      </select>
                      <select className={inputCls} value={form.birth_year} onChange={(e) => setForm({ ...form, birth_year: e.target.value })} data-testid="booking-birth-year">
                        <option value="">{t("profile.birthYear")}</option>
                        {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                      </select>
                      <select className={inputCls} value={form.birth_month} onChange={(e) => setForm({ ...form, birth_month: e.target.value })} data-testid="booking-birth-month">
                        <option value="">{t("profile.birthMonth")}</option>
                        {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                      <select className={inputCls} value={form.birth_day} onChange={(e) => setForm({ ...form, birth_day: e.target.value })} data-testid="booking-birth-day">
                        <option value="">{t("profile.birthDay")}</option>
                        {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
                      </select>
                      <select className={inputCls} value={form.birth_hour} onChange={(e) => setForm({ ...form, birth_hour: e.target.value })} data-testid="booking-birth-hour">
                        <option value="">{t("profile.birthHour")}</option>
                        {HOURS.map((h) => <option key={h} value={h}>{`${String(h).padStart(2, "0")}:00`}</option>)}
                      </select>
                    </div>
                  </div>

                  <textarea rows={3} placeholder={t("booking.concern")} value={form.concern} onChange={(e) => setForm({ ...form, concern: e.target.value })} className={inputCls} data-testid="booking-concern" />
                  <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#005870] text-white font-medium hover:bg-[#003645] disabled:opacity-60" data-testid="booking-submit">
                    {submitting && <Loader2 size={14} className="animate-spin" />} {t("booking.submit")}
                  </button>
                </form>
              )}
            </div>
          )}
        </>
      )}

      <div className="mt-16">
        <Link to="/dashboard" className="text-sm text-[#005870] hover:text-[#00868e] font-medium">
          {t("booking.myBookings")} →
        </Link>
      </div>
    </div>
  );
}
